import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/pro-solid-svg-icons";

interface SimpleAccordionProps {
  /**
   * The title content to display in the accordion header
   */
  title: React.ReactNode;
  /**
   * The content to display when expanded
   */
  children: React.ReactNode;
  /**
   * Additional classes for the accordion container
   */
  className?: string;

  /** CSS class name for the container of the accordion header/title for background colour changing */
  titleContainerBackgroundColourClassName?: string;

  /** CSS class name for the container of the accordion body for background colour changing */
  bodyContainerBackgroundColourClassName?: string;

  /**
   * Controls if the accordion is open (controlled component)
   */
  isOpen?: boolean;
  /**
   * Callback when the open state changes
   */
  onOpenChange?: (isOpen: boolean) => void;
}

const SimpleAccordion: React.FC<SimpleAccordionProps> = ({
  title,
  children,
  className = "",
  titleContainerBackgroundColourClassName = "bg-white",
  bodyContainerBackgroundColourClassName = "bg-white",
  isOpen,
  onOpenChange,
}) => {
  // For controlled or uncontrolled use
  const [isExpanded, setIsExpanded] = useState(isOpen || false);

  // Update local state when controlled prop changes
  useEffect(() => {
    if (isOpen !== undefined) {
      setIsExpanded(isOpen);
    }
  }, [isOpen]);

  const toggleAccordion = (e: React.MouseEvent) => {
    // Don't toggle if the click is on a button
    if ((e.target as HTMLElement).closest("button")) {
      return;
    }

    const newExpandedState = !isExpanded;

    // Update local state if uncontrolled
    if (isOpen === undefined) {
      setIsExpanded(newExpandedState);
    }

    // Notify parent of change if controlled
    if (onOpenChange) {
      onOpenChange(newExpandedState);
    }
  };

  return (
    <div className={`${className}`}>
      <div className="rounded-md border border-gray-200 hover:shadow-sm">
        {/* Accordion Header */}
        <div
          className={`${titleContainerBackgroundColourClassName} flex items-center justify-between w-full cursor-pointer`}
          onClick={toggleAccordion}
        >
          <div className="w-full">{title}</div>
          <div className="flex items-center mr-4 text-gray-500">
            <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
          </div>
        </div>

        {/* Accordion Content */}
        {isExpanded && (
          <div
            className={`${bodyContainerBackgroundColourClassName} border-t border-gray-200`}
          >
            {children}
          </div>
        )}
      </div>
    </div>
  );
};

export default SimpleAccordion;
